import React, { useState, useEffect, useMemo } from "react";
import { List } from "@refinedev/mui";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslate, useNavigation, useNotification } from "@refinedev/core";
import { Chip, IconButton, Stack, Tooltip } from "@mui/material";
import { Edit, Trash2 } from "lucide-react";
import axiosInstance from "../../api/axios-config";

interface Provider {
  id: number;
  code: string;
  name: string;
  protocolType: string;
  messageFormat: string;
  isActive: boolean;
  createdAt: string;
  _count: {
    userMonitorings: number;
    operatingCompanies: number;
  };
}

export const ProviderList: React.FC = () => {
  const translate = useTranslate();
  const { edit, create } = useNavigation();
  const { open } = useNotification();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/monitoring/providers/all");
      setProviders(res.data?.data || []);
    } catch {
      open?.({ type: "error", message: "Erreur lors du chargement des providers" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProviders(); }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Désactiver ce provider ?")) return;
    try {
      await axiosInstance.delete(`/monitoring/providers/${id}`);
      open?.({ type: "success", message: "Provider désactivé" });
      fetchProviders();
    } catch {
      open?.({ type: "error", message: "Erreur lors de la désactivation" });
    }
  };

  const columns = useMemo<GridColDef[]>(() => [
    { field: "code", headerName: "Code", flex: 0.5, minWidth: 100 },
    { field: "name", headerName: "Nom", flex: 1, minWidth: 180 },
    { field: "protocolType", headerName: "Protocole", flex: 0.5, minWidth: 120 },
    { field: "messageFormat", headerName: "Format", flex: 0.5, minWidth: 120 },
    {
      field: "isActive",
      headerName: "Statut",
      flex: 0.4,
      minWidth: 100,
      renderCell: ({ value }) => (
        <Chip
          label={value ? "Actif" : "Inactif"}
          color={value ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "_count",
      headerName: "Utilisateurs",
      flex: 0.4,
      minWidth: 100,
      renderCell: ({ row }) => row._count?.userMonitorings ?? 0,
    },
    {
      field: "companies",
      headerName: "Entreprises",
      flex: 0.4,
      minWidth: 100,
      renderCell: ({ row }) => row._count?.operatingCompanies ?? 0,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.4,
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Modifier">
            <IconButton size="small" onClick={() => edit("telesurveilleur", row.id)}>
              <Edit size={16} />
            </IconButton>
          </Tooltip>
          {row.isActive && (
            <Tooltip title="Désactiver">
              <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                <Trash2 size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ], []);

  return (
    <List
      title="Providers de télésurveillance"
      createButtonProps={{ onClick: () => create("telesurveilleur") }}
    >
      <DataGrid
        rows={providers}
        columns={columns}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        disableRowSelectionOnClick
      />
    </List>
  );
};
